import { BoundingSphere, Math as CMath, Cartesian3, HeadingPitchRange, } from "cesium";
import { AssetType, } from "../types";
import { Drawing2DImage } from "./drawing-2d-Image/drawing-2d-image";
import { RoadStructure } from "./road-structure";
import { StructureManager } from "./structure-manager";
import { ReferenceTopologyManager } from "./tileset/reference-topology-manager";
export class Site {
    _trenchMap;
    _roadMap;
    _flatGroundMap;
    _lineWorksMap;
    _pointCloudMap = new Map();
    _designDataMap = new Map();
    _dxfMap = new Map();
    _structureManager;
    // private _drawing2DImageManager: Drawing2DImageManager;
    _referenceTopologyManager;
    _solidMeshMap = new Map();
    _spreadingCompactionMap = new Map();
    _worldTransformCallback;
    _localTransformCallback;
    _hightMapActivation;
    static _instance;
    get structureManager() {
        return this._structureManager;
    }
    constructor(localCallback, worldCallback) {
        this._trenchMap = new Map();
        this._roadMap = new Map();
        this._flatGroundMap = new Map();
        this._pointCloudMap = new Map();
        this._designDataMap = new Map();
        this._dxfMap = new Map();
        this._lineWorksMap = new Map();
        this._structureManager = new StructureManager();
        this._referenceTopologyManager = new ReferenceTopologyManager();
        this._worldTransformCallback = worldCallback;
        this._localTransformCallback = localCallback;
        this._hightMapActivation = false;
    }
    static getInstance(localCallback, worldCallback) {
        if (!Site._instance && localCallback && worldCallback) {
            Site._instance = new Site(localCallback, worldCallback);
        }
        if (!Site._instance) {
            throw new Error("Site is not initialized");
        }
        return Site._instance;
    }
    isHeightMapActive() {
        return this._hightMapActivation;
    }
    setHightMapInactivate() {
        this._hightMapActivation = false;
    }
    setHightMapActivate() {
        this._hightMapActivation = true;
    }
    isHeightMapExists(id) {
        return this._pointCloudMap.get(id) !== undefined;
    }
    isHightMapVisible(id) {
        const pointCloudHMap = this._pointCloudMap.get(id);
        return (pointCloudHMap !== undefined &&
            pointCloudHMap.heightMap.isHeightMapPrimitiveExists());
    }
    toggleHightMapDisplay(viewer, id) {
        if (this.isHightMapVisible(id)) {
            this.disposeHeightMap(id, AssetType.Point_Cloud, viewer);
            return false;
        }
        else {
            this.renderHeightMap(id, AssetType.Point_Cloud, "red", viewer);
            return true;
        }
    }
    renderHeightMap(id, type, color, viewer) {
        let entity = undefined;
        switch (type) {
            case AssetType.Point_Cloud:
                entity = this._pointCloudMap.get(id);
                break;
            case AssetType.Design_Data:
                entity = this._designDataMap.get(id);
                break;
            case AssetType.Road_Data:
                entity = this._roadMap.get(id);
                break;
            case AssetType.Flat_Ground_Data:
                entity = this._flatGroundMap.get(id);
                break;
            case AssetType.Trench_Data:
                entity = this._trenchMap.get(id);
                break;
            case AssetType.REFERENCE_TOPOLOGY:
                entity = this._referenceTopologyManager.getReferenceTopology(id);
                break;
        }
        if (entity) {
            entity.heightMap
                .render(color, this, viewer)
                .then(() => {
                console.log("Height map rendered");
            })
                .catch((e) => {
                console.warn({ error: e });
            });
        }
        else {
            console.warn(`Incorrect id ${id} or type ${type}. Please check`);
        }
    }
    disposeHeightMap(id, type, viewer) {
        let entity = undefined;
        switch (type) {
            case AssetType.Point_Cloud:
                entity = this._pointCloudMap.get(id);
                break;
            case AssetType.Design_Data:
                entity = this._designDataMap.get(id);
                break;
            case AssetType.Road_Data:
                entity = this._roadMap.get(id);
                break;
            case AssetType.Flat_Ground_Data:
                entity = this._flatGroundMap.get(id);
                break;
            case AssetType.Trench_Data:
                entity = this._trenchMap.get(id);
                break;
        }
        if (entity) {
            entity.heightMap.dispose(viewer);
        }
    }
    getConnectionsInSiteJSON() {
        return this.structureManager.getConnectionsJSON();
    }
    loadConnectionsInSiteJSON(jsonStr) {
        return this.structureManager.loadConnectionsJSON(jsonStr);
    }
    async convertPointsToWGS84(pts) {
        const res = await this._worldTransformCallback(pts);
        return res.map((r) => new Cartesian3(r.x, r.y, r.z));
    }
    /**
     * CRS conversion seam for injection into SolidMesh/builder.
     * Thin wrapper over convertPointsToWGS84/convertPointsToLocalSpace so the
     * solid-mesh module depends on ICrsConverter instead of the Site singleton.
     */
    get crsConverter() {
        return {
            toWgs84: (points) => this.convertPointsToWGS84([...points]),
            toLocal: (points) => this.convertPointsToLocalSpace([...points]),
        };
    }
    async convertLocalGeometryToWgs84(localGeometry) {
        return this.transformGeometry(localGeometry, (pts) => this.convertPointsToWGS84(pts));
    }
    async convertWgs84GeometryToLocal(wgs84Geometry) {
        return this.transformGeometry(wgs84Geometry, (pts) => this.convertPointsToLocalSpace(pts));
    }
    async transformGeometry(geometry, transformPoints) {
        const points = Cartesian3.unpackArray(geometry.vertices);
        const transformed = await transformPoints(points);
        return {
            vertices: Cartesian3.packArray(transformed),
            indices: [...geometry.indices],
        };
    }
    async convertPointsToLocalSpace(pts) {
        const res = await this._localTransformCallback(pts);
        return res.map((r) => new Cartesian3(r.x, r.y, r.z));
    }
    //Arbitrary Line
    addArbitraryLine(arbitraryLine) {
        this._lineWorksMap.set(arbitraryLine.id, arbitraryLine);
    }
    getArbitraryLine(id) {
        return this._lineWorksMap.get(id);
    }
    deleteArbitraryLine(id) {
        const arbitraryLine = this._lineWorksMap.get(id);
        arbitraryLine?.dispose();
        this._lineWorksMap.delete(id);
    }
    //Trench
    addTrench(trench) {
        this._trenchMap.set(trench.id, trench);
    }
    getTrench(id) {
        return this._trenchMap.get(id);
    }
    deleteTrench(id) {
        const trench = this._trenchMap.get(id);
        trench?.dispose();
        this._trenchMap.delete(id);
    }
    getTrenches() {
        return Array.from(this._trenchMap.values());
    }
    disposeTrenches() {
        this._trenchMap.forEach((p) => p.dispose());
        this._trenchMap.clear();
    }
    //Road
    addRoad(road) {
        this._roadMap.set(road.id, road);
        this.structureManager.addStructure(road.id, new RoadStructure(road));
    }
    getRoad(id) {
        return this._roadMap.get(id);
    }
    deleteRoad(id) {
        const road = this._roadMap.get(id);
        road?.dispose();
        this.structureManager.deleteStructure(id);
        // this.getRoads().forEach((road) => {
        //     road.deleteRoadFromChildRoadMap(id);
        // });
        this._roadMap.delete(id);
    }
    getRoads() {
        return Array.from(this._roadMap.values());
    }
    disposeRoads() {
        this._roadMap.forEach((p) => p.dispose());
        this._roadMap.clear();
    }
    getAlignment(id) {
        if (this._roadMap.has(id)) {
            return this._roadMap.get(id)?.alignment;
        }
        if (this._trenchMap.has(id)) {
            return this._trenchMap.get(id)?.alignment;
        }
        throw new Error(`Alignment with id ${id} not found`);
    }
    getCesiumRenderableAlignment(id) {
        if (this._roadMap.has(id)) {
            return this._roadMap.get(id)?.cesiumRenderableAlignment;
        }
        if (this._trenchMap.has(id)) {
            return this._trenchMap.get(id)?.cesiumRenderableAlignment;
        }
        throw new Error(`Alignment with id ${id} not found`);
    }
    //FlatGround
    addFlatGround(flatGround) {
        this._flatGroundMap.set(flatGround.id, flatGround);
    }
    getFlatGround(id) {
        return this._flatGroundMap.get(id);
    }
    deleteFlatGround(id) {
        const flatGround = this._flatGroundMap.get(id);
        flatGround?.dispose();
        this._flatGroundMap.delete(id);
    }
    getFlatGrounds() {
        return Array.from(this._flatGroundMap.values());
    }
    disposeFlatGrounds() {
        this._flatGroundMap.forEach((f) => f.dispose());
        this._flatGroundMap.clear();
    }
    // --- Spreading Compaction ---
    addSpreadingCompaction(id, spreadingCompaction) {
        this._spreadingCompactionMap.set(id, spreadingCompaction);
    }
    getSpreadingCompaction(id) {
        return this._spreadingCompactionMap.get(id);
    }
    deleteSpreadingCompaction(id) {
        const spreadingCompaction = this._spreadingCompactionMap.get(id);
        spreadingCompaction?.disposeAll();
        this._spreadingCompactionMap.delete(id);
    }
    getSpreadingCompactions() {
        return Array.from(this._spreadingCompactionMap.values());
    }
    disposeSpreadingCompactions() {
        this._spreadingCompactionMap.forEach((spreadingCompaction) => spreadingCompaction.disposeAll());
        this._spreadingCompactionMap.clear();
    }
    getDrawing2dImage(id) {
        const referenceTopology = this._referenceTopologyManager.getReferenceTopology(id);
        return referenceTopology?.getDrawing2DImage();
    }
    async addReferenceTopology(args, viewer, cesiumPointSelectorFactory) {
        if (this._referenceTopologyManager.hasReferenceTopology(args.assetId)) {
            throw new Error(`ReferenceTopology with id ${args.assetId} already exists`);
        }
        const cameraOffset = new HeadingPitchRange(CMath.toRadians(0.0), CMath.toRadians(-90.0), 1000.0);
        const boundingSphere = BoundingSphere.fromPoints([
            Cartesian3.fromDegrees(args.centerLongitude, args.centerLatitude, 0),
        ]);
        viewer.camera.flyToBoundingSphere(boundingSphere, {
            duration: 0.1,
            offset: cameraOffset,
            complete: async () => {
                console.log("Camera moved");
                const drawing2DImage = await Drawing2DImage.create(args, viewer, this, cesiumPointSelectorFactory);
                this._referenceTopologyManager.addReferenceTopology(drawing2DImage);
            },
        });
    }
    getReferenceTopology(id) {
        return this._referenceTopologyManager.getReferenceTopology(id);
    }
    getReferenceTopologies() {
        return this._referenceTopologyManager.getReferenceTopologies();
    }
    hasReferenceTopology(id) {
        return this._referenceTopologyManager.hasReferenceTopology(id);
    }
    deleteReferenceTopology(id) {
        this._referenceTopologyManager.deleteReferenceTopology(id);
    }
    async addReferenceTopologyFromJSON(assetId, file, args, viewer, cesiumPointSelectorFactory) {
        await Drawing2DImage.fromJSONWithCheckTileAvailability(assetId, file, args, viewer, this, cesiumPointSelectorFactory, (drawing2DImage) => this._referenceTopologyManager.addReferenceTopology(drawing2DImage));
    }
    getJSONOfReferenceTopology(id) {
        const image = this.getDrawing2dImage(id);
        return image?.toJSON();
    }
    //pointCloud
    addPointCloud(pointCloud) {
        this._pointCloudMap.set(pointCloud.id, pointCloud);
    }
    getPointCloud(id) {
        return this._pointCloudMap.get(id);
    }
    deletePointCloud(id) {
        const pointCloud = this._pointCloudMap.get(id);
        pointCloud?.dispose();
        this._pointCloudMap.delete(id);
    }
    getPointClouds() {
        return Array.from(this._pointCloudMap.values());
    }
    disposePointClouds() {
        this._pointCloudMap.forEach((p) => p.dispose());
        this._pointCloudMap.clear();
    }
    //designData
    addDesignData(designData) {
        this._designDataMap.set(designData.id, designData);
    }
    getDesignData(id) {
        return this._designDataMap.get(id);
    }
    deleteDesignData(id) {
        const designData = this._designDataMap.get(id);
        designData?.dispose();
        this._designDataMap.delete(id);
    }
    getDesignDatas() {
        return Array.from(this._designDataMap.values());
    }
    disposeDesignDatas() {
        this._designDataMap.forEach((p) => p.dispose());
        this._designDataMap.clear();
    }
    getActiveDesignData() {
        const designTileset = this.getDesignDatas()[0];
        if (!designTileset) {
            console.warn("design Data tileset not found. Please upload a pointcloud to perform this operation");
        }
        return designTileset;
    }
    disposeAll() {
        this.disposeRoads();
        this.disposeFlatGrounds();
        this.disposePointClouds();
        this.disposeDesignDatas();
        this.disposeSpreadingCompactions();
    }
    //dxf
    addDXFData(id, dxfData) {
        this._dxfMap.set(id, dxfData);
    }
    deleteDXFData(id) {
        const dxfData = this._dxfMap.get(id);
        dxfData?.dispose();
        this._dxfMap.delete(id);
    }
    getDXFData(id) {
        const dxf = this._dxfMap.get(id);
        if (!dxf) {
            console.warn(`DXF not found: ${id}`);
        }
        return dxf;
    }
    getActiveHeightMaps() {
        const heightMap = new Map();
        const pointCloudHeightMaps = this.getPointClouds()
            .filter((p) => p.tileSet.show)
            .map((p) => p.heightMap);
        heightMap.set(AssetType.Point_Cloud, pointCloudHeightMaps);
        const designHeightMaps = this.getDesignDatas()
            .filter((d) => d.tileSet.show)
            .map((d) => d.heightMap);
        heightMap.set(AssetType.Design_Data, designHeightMaps);
        const roadHeightMaps = this.getRoads()
            .filter((r) => r.isVisible())
            .map((r) => r.heightMap);
        heightMap.set(AssetType.Road_Data, roadHeightMaps);
        const flatGroundHeightMaps = this.getFlatGrounds()
            .filter((f) => f.isVisible())
            .map((f) => f.heightMap);
        heightMap.set(AssetType.Flat_Ground_Data, flatGroundHeightMaps);
        const trenchHeightMaps = this.getTrenches()
            .filter((t) => t.isVisible)
            .map((t) => t.heightMap);
        heightMap.set(AssetType.Trench_Data, trenchHeightMaps);
        return heightMap;
    }
    getActiveTraceableMaps() {
        const traceableMap = new Map();
        const pointCloudHeightMaps = this.getPointClouds()
            .filter((p) => p.tileSet.show)
            .map((p) => p.heightMap);
        traceableMap.set(AssetType.Point_Cloud, pointCloudHeightMaps);
        const designHeightMaps = this.getDesignDatas().filter((d) => d.tileSet.show);
        traceableMap.set(AssetType.Design_Data, designHeightMaps);
        const roadHeightMaps = this.getRoads().filter((r) => r.isVisible());
        traceableMap.set(AssetType.Road_Data, roadHeightMaps);
        const flatGroundHeightMaps = this.getFlatGrounds().filter((f) => f.isVisible());
        traceableMap.set(AssetType.Flat_Ground_Data, flatGroundHeightMaps);
        const trenchHeightMaps = this.getTrenches().filter((t) => t.isVisible);
        traceableMap.set(AssetType.Trench_Data, trenchHeightMaps);
        const visibleReferenceTopologis = this._referenceTopologyManager
            .getReferenceTopologies()
            .filter((r) => r.getDrawing2DImage().isVisible());
        traceableMap.set(AssetType.REFERENCE_TOPOLOGY, visibleReferenceTopologis);
        // Compaction surfaces only — spreading is excluded from cross-section tracing.
        const spreadingCompactionTraceables = this.getSpreadingCompactions()
            .filter((spreadingCompaction) => spreadingCompaction.visible)
            .flatMap((spreadingCompaction) => spreadingCompaction
            .getTraceables()
            .filter((t) => t.type === "compaction"));
        traceableMap.set(AssetType.Spreading_Compaction_Data, spreadingCompactionTraceables);
        return traceableMap;
    }
    getVisibleRayCastCapables() {
        const map = new Map();
        const pointClouds = this.getPointClouds().filter((p) => p.tileSet.show);
        const designDatas = this.getDesignDatas().filter((d) => d.tileSet.show);
        const roads = this.getRoads().filter((r) => r.isVisible());
        const flatGrounds = this.getFlatGrounds().filter((f) => f.isVisible());
        const trenches = this.getTrenches().filter((t) => t.isVisible);
        const referenceTopologies = this._referenceTopologyManager
            .getReferenceTopologies()
            .filter((rt) => rt.getDrawing2DImage().isVisible());
        map.set(AssetType.Point_Cloud, pointClouds);
        map.set(AssetType.Design_Data, designDatas);
        map.set(AssetType.Road_Data, roads);
        map.set(AssetType.Flat_Ground_Data, flatGrounds);
        map.set(AssetType.Trench_Data, trenches);
        map.set(AssetType.REFERENCE_TOPOLOGY, referenceTopologies);
        return map;
    }
    getTerrain(id, terrainType) {
        let terrain;
        switch (terrainType) {
            case AssetType.Point_Cloud:
                terrain = this.getPointCloud(id);
                break;
            case AssetType.Design_Data:
                terrain = this.getDesignData(id);
                break;
            case AssetType.REFERENCE_TOPOLOGY:
                terrain = this.getReferenceTopology(id);
                break;
            default:
                throw new Error(`Invalid terrain type: ${terrainType}`);
        }
        return terrain;
    }
    //SolidMesh
    addSolidMesh(solidMesh) {
        // Centralize replace logic here to avoid duplicate delete-before-add
        // checks elsewhere (prevents races where handler and site both delete).
        const existing = this._solidMeshMap.get(solidMesh.id);
        // If the same instance is re-added, do nothing.
        if (existing === solidMesh)
            return;
        if (existing) {
            try {
                existing.dispose();
            }
            catch (e) {
                console.error("Error disposing existing solid mesh:", e);
            }
            this._solidMeshMap.delete(solidMesh.id);
        }
        this._solidMeshMap.set(solidMesh.id, solidMesh);
    }
    /** Returns the solid mesh; throws when it does not exist. */
    getSolidMesh(id) {
        const solidMesh = this._solidMeshMap.get(id);
        if (!solidMesh) {
            throw new Error(`SolidMesh not found: ${id}`);
        }
        return solidMesh;
    }
    /** Returns the solid mesh, or undefined when absent (tolerant lookup). */
    findSolidMesh(id) {
        return this._solidMeshMap.get(id);
    }
    deleteSolidMesh(id) {
        const solidMesh = this._solidMeshMap.get(id);
        if (solidMesh) {
            solidMesh.dispose();
            this._solidMeshMap.delete(id);
        }
    }
}
//# sourceMappingURL=site.js.map